<script setup lang="ts">
/**
 * ANCIEN MODÈLE de la page cagnotte (en ligne jusqu'au 2026-10-13) : héros
 * mobile, statistiques, onglets, carte de don collante à droite, barre
 * collante en bas. GARDÉ tel quel au cas où (Karim 2026-10-13) — activable
 * par `MODELE_PAGE_CAGNOTTE = "ancien"` (utils/modelePageCagnotte.ts).
 *
 * La page (`pages/c/[slug]/index.vue`) garde ce qui est commun à tous les
 * modèles : chargement, référencement, aperçu du dashboard, états d'erreur.
 */
import type { Campaign } from "~/types/campaign";
import type { useCampaignPublic } from "~/composables/useCampaignPublic";
import { navLinks } from "~/utils/navLinks";

const props = defineProps<{
  campagne: Campaign;
  controleur: ReturnType<typeof useCampaignPublic>;
}>();

const {
  donorsSortBy,
  allDonors,
  donorsTotal,
  isLoadingDonors,
  isLoadingMoreDonors,
  topDonor,
  hasMoreDonors,
  galleryImages,
  hasUpdates,
  campaignImageUrl,
  youtubeEmbedUrl,
  progressPercent,
  launchDateText,
  closesAt,
  endDateLabel,
  marquerTerminee,
  isCampaignClosed,
  isCampaignPaused,
  hasGoal,
  organizationLogo,
  organizationName,
  organizationSlug,
  formatAmountShort,
  formatAmountCompact,
  formatCountCompact,
  getRelativeTime,
  formatUpdateDate,
  loadMoreDonors,
} = props.controleur;

const campaign = computed(() => props.campagne);

// Hero images = image principale + galerie (pour le carousel de la nouvelle UI)
const heroImages = computed(() => {
  const list: { id: string; url: string }[] = [];
  if (campaignImageUrl.value) {
    list.push({ id: "main", url: campaignImageUrl.value });
  }
  for (const item of galleryImages.value) {
    list.push({ id: item.id, url: item.url });
  }
  return list;
});

// Sticky bar visibility on scroll
const showStickyBar = ref(false);
const mainCardRef = ref<HTMLElement | null>(null);
const donorsListAnchor = ref<HTMLElement | null>(null);

function scrollToDonorsList(sort: "recent" | "highest") {
  donorsSortBy.value = sort;
  nextTick(() => {
    donorsListAnchor.value?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  });
}

// Share bottom sheet
const shareBottomSheetOpen = ref(false);
const openShareBottomSheet = () => {
  shareBottomSheetOpen.value = true;
};

// CTA "Je participe" : navigation vers la page de paiement. Les composants
// (MobileActions / DonationCard) émettent `donate` (au lieu d'embarquer la
// navigation) → la PAGE décide. Karim 2026-06-21.
function goToDonate() {
  navigateTo(`/c/${campaign.value.slug}/payment`);
}

onMounted(() => {
  // Observer for sticky bar
  const handleScroll = () => {
    if (mainCardRef.value) {
      const rect = mainCardRef.value.getBoundingClientRect();
      showStickyBar.value = rect.bottom < 0;
    } else {
      // Fallback desktop : pas de mainCardRef visible → on se base sur scrollY
      showStickyBar.value = window.scrollY > 500;
    }
  };

  window.addEventListener("scroll", handleScroll, { passive: true });
  onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
  });
});
</script>

<template>
  <div class="grow flex flex-col bg-gray-50">
    <AppHeader :links="navLinks" disable-scroll-hide mobile-transparent rayon="cagnottes" />

    <CampaignStickyBar
      :campaign="campaign"
      :progress-percent="progressPercent"
      :is-campaign-closed="isCampaignClosed"
      :is-campaign-paused="isCampaignPaused"
      :has-goal="hasGoal"
      :format-amount-short="formatAmountShort"
      :format-amount-compact="formatAmountCompact"
      :visible="showStickyBar"
      :donors="allDonors"
      @open-share="openShareBottomSheet"
      @donate="goToDonate"
    />

    <!-- ================= MOBILE (< lg) ================= -->
    <div class="lg:hidden">
      <div ref="mainCardRef">
        <CampaignMobileHero
          :images="heroImages"
          :title="campaign.title"
          :organization-logo="organizationLogo"
          :organization-name="organizationName"
          :organization-slug="organizationSlug"
        />
      </div>

      <div class="px-4 pt-5 pb-16 space-y-5">
        <CampaignMobileStats
          :campaign="campaign"
          :progress-percent="progressPercent"
          :has-goal="hasGoal"
          :format-amount-short="formatAmountShort"
          :format-amount-compact="formatAmountCompact"
          :get-relative-time="getRelativeTime"
          :show-amount-raised="campaign.show_amount_raised"
          :donors="allDonors"
        />

        <!-- Barre de temps jusqu'à la clôture (date de fin posée).
             `px-3.5` : rentrée là où commence l'arrondi des boutons « Je
             participe / Partager » juste dessous (Karim 2026-10-09). -->
        <CampaignCountdown
          v-if="closesAt && endDateLabel && !isCampaignClosed && !isCampaignPaused"
          class="px-3.5"
          :closes-at="closesAt"
          :starts-at="campaign.start_date || campaign.date_created || campaign.created_at || null"
          :end-date-label="endDateLabel"
          :days-remaining="campaign.days_remaining ?? null"
          compact
          @ended="marquerTerminee"
        />

        <CampaignMobileActions
          :campaign="campaign"
          :is-campaign-closed="isCampaignClosed"
          :is-campaign-paused="isCampaignPaused"
          @open-share="openShareBottomSheet"
          @donate="goToDonate"
        />

        <CampaignTabContent
          :campaign="campaign"
          :has-updates="hasUpdates"
          :youtube-embed-url="youtubeEmbedUrl"
          :format-update-date="formatUpdateDate"
          :is-campaign-closed="isCampaignClosed"
          :is-campaign-paused="isCampaignPaused"
          @open-share="openShareBottomSheet"
          @donate="goToDonate"
        />

        <Transition
          enter-active-class="transition-all duration-500 ease-out"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
        >
          <CampaignDonorsBanner
            v-if="campaign.show_donors_list && donorsTotal > 0"
            :donors="allDonors"
            :donors-total="donorsTotal"
          />
        </Transition>

        <!-- Navigateur seulement : les donateurs se chargent côté client, le
             serveur rendait autre chose (décalage d'hydratation). -->
        <ClientOnly>
        <Transition
          enter-active-class="transition-all duration-500 ease-out delay-100"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
        >
          <CampaignDonorsList
            v-if="campaign.show_donors_list && (allDonors.length > 0 || isLoadingDonors)"
            v-model:sort-by="donorsSortBy"
            :donors="allDonors"
            :donors-total="donorsTotal"
            :top-donor="topDonor"
            :is-loading="isLoadingDonors"
            :is-loading-more="isLoadingMoreDonors"
            :has-more="hasMoreDonors"
            :format-amount-short="formatAmountShort"
            :get-relative-time="getRelativeTime"
            @load-more="loadMoreDonors"
          />
        </Transition>
        </ClientOnly>

        <CampaignThankYouMessage
          v-if="campaign.custom_thank_you_message"
          :message="campaign.custom_thank_you_message"
        />
      </div>
    </div>

    <!-- ================= DESKTOP (≥ lg) ================= -->
    <div class="hidden lg:block w-full max-w-6xl mx-auto px-4 lg:px-6 pt-6 lg:pt-10 pb-16 lg:pb-24">
      <!-- Titre en pleine largeur -->
      <h1 class="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6 lg:mb-8">
        {{ campaign.title }}
      </h1>

      <!-- Layout 2 colonnes -->
      <div class="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <!-- Colonne gauche -->
        <div class="min-w-0 space-y-6">
          <CampaignHeroImage :images="heroImages" :is-verified="campaign.is_verified" />

          <div class="flex items-center justify-between gap-3 flex-wrap">
            <CampaignCreator
              :organization-logo="organizationLogo"
              :organization-name="organizationName"
              :organization-slug="organizationSlug"
              :launch-date-text="launchDateText"
            />
            <CampaignSecurityBadge :is-verified="campaign.is_verified" />
          </div>

          <CampaignTabContent
            :campaign="campaign"
            :has-updates="hasUpdates"
            :youtube-embed-url="youtubeEmbedUrl"
            :format-update-date="formatUpdateDate"
            :is-campaign-closed="isCampaignClosed"
            :is-campaign-paused="isCampaignPaused"
            @open-share="openShareBottomSheet"
            @donate="goToDonate"
          />

          <div ref="donorsListAnchor" class="space-y-4">
            <Transition
              enter-active-class="transition-all duration-500 ease-out"
              enter-from-class="opacity-0 translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
            >
              <CampaignDonorsBanner
                v-if="campaign.show_donors_list && donorsTotal > 0"
                :donors="allDonors"
                :donors-total="donorsTotal"
              />
            </Transition>

            <ClientOnly>
            <Transition
              enter-active-class="transition-all duration-500 ease-out delay-100"
              enter-from-class="opacity-0 translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
            >
              <CampaignDonorsList
                v-if="campaign.show_donors_list && (allDonors.length > 0 || isLoadingDonors)"
                v-model:sort-by="donorsSortBy"
                :donors="allDonors"
                :donors-total="donorsTotal"
                :top-donor="topDonor"
                :is-loading="isLoadingDonors"
                :is-loading-more="isLoadingMoreDonors"
                :has-more="hasMoreDonors"
                :format-amount-short="formatAmountShort"
                :get-relative-time="getRelativeTime"
                @load-more="loadMoreDonors"
              />
            </Transition>
            </ClientOnly>
          </div>

          <CampaignThankYouMessage
            v-if="campaign.custom_thank_you_message"
            :message="campaign.custom_thank_you_message"
          />
        </div>

        <!-- Colonne droite : carte sticky -->
        <div class="min-w-0">
          <div class="sticky top-6">
            <!-- Navigateur seulement : elle affiche les donateurs, chargés côté
                 client (décalage d'hydratation sinon). -->
            <ClientOnly>
            <CampaignDonationCard
              :campaign="campaign"
              :progress-percent="progressPercent"
              :closes-at="closesAt"
              :end-date-label="endDateLabel"
              :is-campaign-closed="isCampaignClosed"
              :is-campaign-paused="isCampaignPaused"
              :has-goal="hasGoal"
              :format-amount-short="formatAmountShort"
              :format-amount-compact="formatAmountCompact"
              :format-count-compact="formatCountCompact"
              :get-relative-time="getRelativeTime"
              :show-amount-raised="campaign.show_amount_raised"
              :donors="allDonors"
              :donors-total="donorsTotal"
              :top-donor="topDonor"
              :is-loading-donors="isLoadingDonors"
              @open-share="openShareBottomSheet"
              @ended="marquerTerminee"
              @donate="goToDonate"
              @see-all="scrollToDonorsList('recent')"
              @see-top="scrollToDonorsList('highest')"
            />
            </ClientOnly>
          </div>
        </div>
      </div>
    </div>

    <CampaignShareBottomSheet v-model:open="shareBottomSheetOpen" :campaign="campaign" />
  </div>
</template>
